import React from "react";
import moment from "moment";
import * as M from "../../Modal";
import * as H from "../../../hooks";
import * as C from "../../../Common";
import * as BS from "react-bootstrap";
import * as S from "../../../services";
import { DataSources, RIGHTS, T, TC } from "../../../Constants";

export type DataSourceProps = {
    /** The id of the active submission */
    submission_id: string;
    /** Don't allow to edit the datasources */
    read_only?: boolean;
}

const DataSource: React.FC<DataSourceProps> = props => {
    const lg = H.useLanguage();
    const [roots] = H.useRoots();
    const rights = H.useRights();
    const [{ userId }] = H.useAuth();
    const [sources, set_sources, status] = H.useAsyncState<T.DataSource[]>([]);

    React.useEffect(() => {
        let isSubscribed = true;
        S.getDatasources(props.submission_id)
            .then(res => isSubscribed && set_sources(res.data, "done"))
            .catch(() => isSubscribed && set_sources([], "error"));
        return () => {
            isSubscribed = false;
            set_sources([], "load");
        }
    }, [set_sources, props.submission_id]);

    const splitted = React.useMemo(() => {
        const datasources: Record<T.DataSource["type"], T.DataSource[]> = { exports: [], datasets: [], documents: [] };
        sources.forEach(source => {
            if (source.type === "datasets") datasources.datasets.push(source);
            else if (source.type === "exports") datasources.exports.push(source);
            else if (source.type === "documents") datasources.documents.push(source);
        });
        return datasources;
    }, [sources]);

    const headers = React.useMemo(() => {
        const datasets_headers = [
            { label: TC.DATASOURCE_LIST_NAME, size: 4 },
            { label: TC.DATASOURCE_LIST_TYPE, size: 3 },
            { label: TC.DATASOURCE_LIST_DATE, size: 3 },
            { label: "", size: 2 },
        ];

        const exports_headers = [
            { label: TC.DATASOURCE_LIST_NAME, size: 2 },
            { label: TC.DATASOURCE_LIST_EXPORT_TYPE, size: 1 },
            { label: TC.DATASOURCE_LIST_FIRST_DATA_DATE, size: 1 },
            { label: TC.DATASOURCE_LIST_FREQUENCY, size: 1 },
            { label: TC.DATASOURCE_LIST_GROUP, size: 1 },
            { label: TC.DATASOURCE_LIST_FORMATTING, size: 1 },
            { label: TC.DATASOURCE_LIST_SEPARATOR, size: 1 },
            { label: TC.DATASOURCE_LIST_DATASETS, size: 1 },
            { label: TC.DATASOURCE_LIST_LAST_EXPORT_DATE, size: 2 },
            { label: TC.DATASOURCE_LIST_STATUS, size: 1 },
        ];

        const dataset_header = <BS.Row className="g-1 fw-bold mb-3">
            {datasets_headers.map((header, index) => <BS.Col key={index} xs={header.size}>{lg.getStaticText(header.label)}</BS.Col>)}
        </BS.Row>;
        const export_header = <BS.Row className="g-1 fw-bold mb-3">
            {exports_headers.map((header, index) => <BS.Col key={index} xs={header.size}>{lg.getStaticText(header.label)}</BS.Col>)}
        </BS.Row>;

        return {
            components: { exports: export_header, datasets: dataset_header, documents: dataset_header },
            sizes: {
                exports: exports_headers.map(h => h.size),
                datasets: datasets_headers.map(h => h.size),
                documents: datasets_headers.map(h => h.size)
            },
        }
    }, [lg]);

    const events = React.useMemo(() => ({
        new_source: (type: T.DataSource["type"]) => {
            // Get the options for the source type
            const options = DataSources.SourceTypes[type];
            // Ask the user to select the source
            M.askSelect({ options, label: TC.DATASOURCE_SOURCE_TYPE, title: TC.DATASOURCE_SOURCE_TYPE, isRequired: true }).then(source => {
                if (source) {
                    const base_datasource: T.DataSource = {
                        type,
                        _id: "",
                        name: "",
                        createdAt: "",
                        updatedAt: "",
                        owner: userId,
                        users: [userId],
                        source_type: source as T.DataSource["source_type"],
                    };

                    M.renderDataSourceForm({ datasource: base_datasource }).then(datasource => {
                        if (datasource) set_sources(p => p.concat(datasource));
                    });
                }
            });
        },
        edit_source: (source: T.DataSource) => {
            M.renderDataSourceForm({ datasource: source }).then(datasource => {
                // Edit the datasource in the list
                if (datasource) set_sources(p => p.map(s => s._id === datasource._id ? datasource : s));
            });
        },
        refresh_source: (source: T.DataSource) => {
            S.refreshDataSource(source._id).then(res => {
                // Show an alert to let the user know the process is asynchronous from now on
                if (res.data === "refreshing") M.renderAlert({ type: "info", message: TC.DATASOURCE_ASYNC_ELEM_FETCHING });
                else M.renderAlert({ type: "warning", message: TC.DATASOURCE_REFRESH_IMPOSSIBLE });
            }).catch(M.Alerts.updateError);
        },
        delete_source: (source: T.DataSource) => {
            M.askConfirm().then(confirmed => {
                if (confirmed) S.deleteDataSource(source._id)
                    .then(() => set_sources(p => p.filter(s => s._id !== source._id)))
                    .catch(M.Alerts.deleteError);
            });
        }
    }), [set_sources, roots, userId]);

    const no_data_panel = React.useMemo(() => <BS.Row className="my-2">
        <BS.Col xs={12}>
            <C.CenterMessage padding={3} bordered size="sm" className="alert-info" children={TC.DATASOURCE_NO_DATA} />
        </BS.Col>
    </BS.Row>, []);

    const user_can = React.useMemo(() => ({
        create: rights.isRightAllowed(RIGHTS.MISC.WRITE_DATA_SOURCE),
        read_others: rights.isRightAllowed(RIGHTS.MISC.READ_OTHER_DATA_SOURCE),
        edit_others: rights.isRightAllowed(RIGHTS.MISC.WRITE_OTHER_DATA_SOURCE),
    }), [rights]);

    return <C.Spinner status={status}>
        <div className="mb-3">
            <C.Flex className="mb-2">
                <C.Title className="mb-0" level={4} text={TC.DATASOURCE_DATASETS_SOURCES} />
                {!props.read_only && <C.Button disabled={!user_can.create} icon="plus" size="sm" onClick={() => events.new_source("datasets")} className="ms-2" />}
            </C.Flex>
            <div className="fs-85 text-start">
                {headers.components.datasets}
                {splitted.datasets.length === 0
                    ? no_data_panel
                    : splitted.datasets.map(src => <BS.Row key={src._id} className="g-1 mb-2">
                        <BS.Col xs={headers.sizes.datasets[0]}>
                            {src.name}
                        </BS.Col>
                        <BS.Col xs={headers.sizes.datasets[1]}>
                            {src.source_type}
                        </BS.Col>
                        <BS.Col xs={headers.sizes.datasets[2]}>
                            {moment(src.createdAt).format("DD/MM/YYYY")}
                        </BS.Col>
                        {!props.read_only && <BS.Col className="d-flex gap-1" xs={headers.sizes.datasets[3]}>
                            <C.Button
                                size="sm"
                                icon="refresh"
                                variant="success"
                                onClick={() => events.refresh_source(src)}
                                disabled={src.owner !== userId && !user_can.edit_others}
                            />
                            <C.Button
                                size="sm"
                                icon="pencil-alt"
                                variant="primary"
                                onClick={() => events.edit_source(src)}
                                disabled={src.owner !== userId && !user_can.edit_others}
                            />
                            <C.Button
                                size="sm"
                                icon="trash-alt"
                                variant="danger"
                                onClick={() => events.delete_source(src)}
                                disabled={src.owner !== userId && !user_can.edit_others}
                            />
                        </BS.Col>}
                    </BS.Row>)}
            </div>
        </div>

        <div className="mb-3">
            <C.Flex className="mb-2">
                <C.Title className="mb-0" level={4} text={TC.DATASOURCE_DOCUMENTS_SOURCES} />
                {!props.read_only && <C.Button disabled={!user_can.create} icon="plus" size="sm" onClick={() => events.new_source("documents")} className="ms-2" />}
            </C.Flex>
            <div className="fs-85 text-start">
                {headers.components.documents}
                {splitted.documents.length === 0
                    ? no_data_panel
                    : splitted.documents.map(src => <BS.Row key={src._id} className="g-1 mb-2">
                        <BS.Col xs={headers.sizes.documents[0]}>
                            {src.name}
                        </BS.Col>
                        <BS.Col xs={headers.sizes.documents[1]}>
                            {src.source_type}
                        </BS.Col>
                        <BS.Col xs={headers.sizes.documents[2]}>
                            {moment(src.createdAt).format("DD/MM/YYYY")}
                        </BS.Col>
                        {!props.read_only && <BS.Col className="d-flex gap-1" xs={headers.sizes.documents[3]}>
                            <C.Button
                                size="sm"
                                icon="refresh"
                                variant="success"
                                onClick={() => events.refresh_source(src)}
                                disabled={src.owner !== userId && !user_can.edit_others}
                            />
                            <C.Button
                                size="sm"
                                icon="pencil-alt"
                                variant="primary"
                                onClick={() => events.edit_source(src)}
                                disabled={src.owner !== userId && !user_can.edit_others}
                            />
                            <C.Button
                                size="sm"
                                icon="trash-alt"
                                variant="danger"
                                onClick={() => events.delete_source(src)}
                                disabled={src.owner !== userId && !user_can.edit_others}
                            />
                        </BS.Col>}
                    </BS.Row>)}
            </div>
        </div>

        {/* TODO: Temporarily disabled */}
        {false && <div className="mb-3">
            <C.Flex className="mb-2">
                <C.Title className="mb-0" level={4} text={TC.DATASETS_EXPORTS_SOURCES} />
                {!props.read_only && <C.Button disabled={!user_can.create} icon="plus" size="sm" onClick={() => events.new_source("exports")} className="ms-2" />}
            </C.Flex>
            <div className="fs-85 text-start">
                {headers.components.exports}
                {splitted.exports.length === 0
                    ? no_data_panel
                    : splitted.exports.map(src => <BS.Row key={src._id} className="g-1 mb-2">
                        <BS.Col xs={headers.sizes.exports[0]}>
                            {/* TODO: BTV */}
                        </BS.Col>
                        <BS.Col xs={headers.sizes.exports[1]}>
                            {/* TODO: BTV */}
                        </BS.Col>
                        <BS.Col xs={headers.sizes.exports[2]}>
                            {/* TODO: BTV */}
                        </BS.Col>
                        <BS.Col xs={headers.sizes.exports[3]}>
                            {/* TODO: BTV */}
                        </BS.Col>
                        <BS.Col xs={headers.sizes.exports[4]}>
                            {/* TODO: BTV */}
                        </BS.Col>
                        <BS.Col xs={headers.sizes.exports[5]}>
                            {/* TODO: BTV */}
                        </BS.Col>
                        <BS.Col xs={headers.sizes.exports[6]}>
                            {/* TODO: BTV */}
                        </BS.Col>
                        <BS.Col xs={headers.sizes.exports[7]}>
                            {/* TODO: BTV */}
                        </BS.Col>
                        <BS.Col xs={headers.sizes.exports[8]}>
                            {/* TODO: BTV */}
                        </BS.Col>
                        <BS.Col xs={headers.sizes.exports[9]}>
                            {/* TODO: BTV */}
                        </BS.Col>
                    </BS.Row>)}
            </div>
        </div>}
    </C.Spinner>
}

export default DataSource;